import { useState } from 'react'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EngineReviewDialog } from './components/engine-review-dialog'
import { ReviewCard } from './components/review-card'
import {
  REVIEW_CACHE_LIMIT,
  useAdminEnginesQuery,
} from './hooks/use-admin-engines'
import { useAdminUnpublish } from './hooks/use-admin-unpublish'
import { useReviewNavigation } from './hooks/use-review-navigation'
import type { AdminEngineItem } from './types/review.types'

const SKELETON_COUNT = 8
const PAGE_SIZE = 20
const route = getRouteApi('/_authenticated/review/')
const SCOPE_OPTIONS = ['pending', 'draft', 'published', 'all'] as const
type ReviewScope = (typeof SCOPE_OPTIONS)[number]

function isReviewScope(value: string): value is ReviewScope {
  return SCOPE_OPTIONS.includes(value as ReviewScope)
}

function matchScope(item: AdminEngineItem, scope: ReviewScope) {
  switch (scope) {
    case 'pending':
      return item.status === 'reviewing'
    case 'draft':
      return item.status === 'draft'
    case 'published':
      return item.status === 'published'
    case 'all':
      return true
    default:
      return false
  }
}

export function Review() {
  const { scope, page } = route.useSearch()
  const navigate = route.useNavigate()

  const list = useAdminEnginesQuery(REVIEW_CACHE_LIMIT)
  const unpublish = useAdminUnpublish()

  const allItems = list.data?.items ?? []
  const totalAll = allItems.length
  const pendingItems = allItems.filter((item) => matchScope(item, 'pending'))
  const pendingCount = pendingItems.length
  const filteredItems = allItems.filter((item) => matchScope(item, scope))
  const total = filteredItems.length
  const pages = Math.ceil(total / PAGE_SIZE)
  const safePage = pages === 0 ? 1 : Math.max(1, Math.min(page, pages))
  const pageStart = (safePage - 1) * PAGE_SIZE
  const pageItems = filteredItems.slice(pageStart, pageStart + PAGE_SIZE)
  const showLimitWarning =
    !list.isLoading && !list.isError && allItems.length === REVIEW_CACHE_LIMIT

  const isEmpty = !list.isLoading && !list.isError && filteredItems.length === 0

  const emptyTitle =
    scope === 'pending'
      ? '暂无待审核内容'
      : scope === 'draft'
        ? '暂无草稿'
        : scope === 'published'
          ? '暂无已发布内容'
          : '暂无引擎'
  const emptyDescription =
    scope === 'pending'
      ? '当前没有待审核的引擎。'
      : scope === 'draft'
        ? '当前没有草稿引擎。'
        : scope === 'published'
          ? '当前没有已发布的引擎。'
          : '当前没有可展示的引擎。'

  const globalNavigate = useNavigate()

  const nav = useReviewNavigation(pendingItems)
  const [unpublishOpen, setUnpublishOpen] = useState(false)
  const [unpublishTarget, setUnpublishTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  // ================================
  // Card intents
  // ================================
  const onView = (engineId: string) => {
    // 查找引擎数据，确定应该查看哪个版本
    const engine = allItems.find((item) => item.id === engineId)

    // 根据引擎状态决定查看哪个版本：
    // - reviewing: 审核中版本
    // - rejected: 被拒绝的版本（也是reviewing版本）
    // - published: 已发布版本
    // - draft: 草稿版本
    let vType: 'draft' | 'reviewing' | 'published' = 'draft'

    if (engine?.status === 'reviewing' || engine?.status === 'rejected') {
      vType = 'reviewing'
    } else if (engine?.status === 'published') {
      vType = 'published'
    }

    navigate({ to: '/editor', search: { engineId, vType } })
  }

  const onUnpublish = (item: { id: string; name: string }) => {
    setUnpublishTarget({ id: item.id, name: item.name })
    setUnpublishOpen(true)
  }

  const setPage = (nextPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage,
        pageSize: PAGE_SIZE,
      }),
    })
  }

  const goPrev = () => setPage(Math.max(1, safePage - 1))
  const goNext = () => setPage(Math.min(pages, safePage + 1))

  const onScopeChange = (value: string) => {
    if (!isReviewScope(value)) return
    if (value === scope) return

    navigate({
      search: (prev) => ({
        ...prev,
        scope: value,
        page: 1,
        pageSize: PAGE_SIZE,
      }),
    })
  }

  return (
    <div className='p-6'>
      <header className='flex flex-col gap-4'>
        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div className='flex min-w-0 items-center gap-3'>
            <Button
              variant='ghost'
              size='sm'
              className='shrink-0'
              onClick={() => globalNavigate({ to: '/' })}
            >
              <ArrowLeft className='mr-1.5 h-4 w-4' />
              返回
            </Button>
            <div className='min-w-0'>
              <h1 className='text-xl font-semibold'>审核</h1>
              <div className='text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-sm'>
                <span>待审核 {pendingCount} 个</span>
                <span>全部（{totalAll}）</span>
              </div>
            </div>
          </div>

          <Button
            variant='outline'
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            {list.isFetching ? '刷新中…' : '刷新'}
          </Button>
        </div>

        <Tabs value={scope} onValueChange={onScopeChange}>
          <TabsList>
            <TabsTrigger value='pending'>待审核</TabsTrigger>
            <TabsTrigger value='draft'>草稿</TabsTrigger>
            <TabsTrigger value='published'>已发布</TabsTrigger>
            <TabsTrigger value='all'>全部</TabsTrigger>
          </TabsList>
        </Tabs>

        {showLimitWarning ? (
          <div className='text-muted-foreground text-xs'>
            当前仅加载最近 {REVIEW_CACHE_LIMIT} 条，可能不包含全部数据
          </div>
        ) : null}
      </header>

      {/* Error */}
      {list.isError ? (
        <div className='mt-6 rounded-xl border p-4'>
          <div className='text-sm font-medium'>加载失败</div>
          <div className='text-muted-foreground mt-1 text-sm'>
            {(list.error as { message?: string } | null)?.message ?? '未知错误'}
          </div>
          <button
            className='hover:bg-muted mt-3 rounded-md border px-3 py-1.5 text-sm'
            onClick={() => list.refetch()}
          >
            重试
          </button>
        </div>
      ) : null}

      {/* Loading Skeleton */}
      {list.isLoading ? (
        <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div
              key={i}
              className='bg-muted/30 overflow-hidden rounded-2xl border'
            >
              <div className='aspect-[3/4] w-full animate-pulse' />
              <div className='space-y-3 p-4'>
                <div className='bg-muted h-4 w-3/4 animate-pulse rounded' />
                <div className='bg-muted h-3 w-full animate-pulse rounded' />
                <div className='bg-muted h-3 w-5/6 animate-pulse rounded' />
                <div className='bg-muted h-4 w-2/3 animate-pulse rounded' />
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Empty */}
      {isEmpty ? (
        <div className='mt-10 rounded-xl border p-10 text-center'>
          <div className='text-base font-medium'>{emptyTitle}</div>
          <div className='text-muted-foreground mt-1 text-sm'>
            {emptyDescription}
          </div>
        </div>
      ) : null}

      {/* Grid */}
      {!list.isLoading && !list.isError && !isEmpty ? (
        <>
          <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            {pageItems.map((item) => (
              <ReviewCard
                key={item.id}
                item={item}
                onView={onView}
                onReview={nav.onReview}
                onUnpublish={onUnpublish}
                unpublishDisabled={unpublish.isPending}
                showReviewAction={item.status === 'reviewing'}
                showUnpublishAction={item.status === 'published'}
                showStatus={scope === 'all'}
              />
            ))}
          </div>

          <div className='mt-6 flex items-center justify-center gap-3'>
            <button
              className='hover:bg-muted rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'
              onClick={goPrev}
              disabled={safePage <= 1 || list.isFetching}
            >
              上一页
            </button>

            <div className='text-muted-foreground text-sm'>
              第 {safePage} / {pages} 页
            </div>

            <button
              className='hover:bg-muted rounded-md border px-3 py-1.5 text-sm disabled:opacity-50'
              onClick={goNext}
              disabled={safePage >= pages || list.isFetching}
            >
              下一页
            </button>
          </div>
        </>
      ) : null}

      <EngineReviewDialog
        open={nav.reviewDialogOpen}
        engineId={nav.reviewEngineId}
        onOpenChange={(open) => {
          nav.setReviewDialogOpen(open)
          if (!open) nav.setReviewEngineId(null)
        }}
        currentIndex={nav.reviewIndex}
        totalCount={pendingItems.length}
        onPrev={nav.goPrev}
        onNext={nav.goNext}
        onSuccess={() => list.refetch()}
      />

      <ConfirmDialog
        open={unpublishOpen}
        onOpenChange={(open) => {
          setUnpublishOpen(open)
          if (!open) setUnpublishTarget(null)
        }}
        title='下架引擎'
        desc={
          unpublishTarget
            ? `确认下架「${unpublishTarget.name}」？下架后会变为草稿状态。`
            : '确认下架该引擎？'
        }
        destructive
        confirmText='确认下架'
        disabled={!unpublishTarget}
        isLoading={unpublish.isPending}
        handleConfirm={() => {
          if (!unpublishTarget) return
          unpublish.unpublish(unpublishTarget.id, {
            onSuccess: () => {
              setUnpublishOpen(false)
              setUnpublishTarget(null)
              list.refetch()
            },
          })
        }}
      />
    </div>
  )
}
